/**
 * Inline SVG brand glyphs. Kept intentionally minimal and monochrome so
 * they inherit `currentColor` and align with the brutalist dark theme.
 * Not brand-accurate on purpose — this is a scaffold.
 */
import { Github, Slack, Send, AudioLines, Cloud, Sparkles, Mail, CreditCard, Video } from "lucide-react";

const iconWrap = (children, props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {children}
  </svg>
);

export function ClickUpGlyph(props) {
  return iconWrap(
    <>
      <path d="M3 14.5l4.5-3 4.5 3 4.5-3 4.5 3" />
      <path d="M3 9.5l4.5-3 4.5 3 4.5-3 4.5 3" />
    </>,
    props,
  );
}

export function ClaudeGlyph(props) {
  return iconWrap(
    <>
      <path d="M12 3v18" />
      <path d="M3 12h18" />
      <path d="M5.6 5.6l12.8 12.8" />
      <path d="M18.4 5.6L5.6 18.4" />
    </>,
    props,
  );
}

export function GeminiGlyph(props) {
  return iconWrap(
    <>
      <path d="M12 2l2.5 6.5L21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5L12 2z" />
    </>,
    props,
  );
}

export function OpenAIGlyph(props) {
  return iconWrap(
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v3M12 18v3M4 8l2.6 1.5M17.4 14.5L20 16M4 16l2.6-1.5M17.4 9.5L20 8" />
    </>,
    props,
  );
}

export function GCPGlyph(props) {
  return iconWrap(
    <>
      <rect x="3" y="3" width="8" height="8" />
      <rect x="13" y="3" width="8" height="8" />
      <rect x="3" y="13" width="8" height="8" />
      <rect x="13" y="13" width="8" height="8" />
    </>,
    props,
  );
}

export function StripeGlyph(props) {
  return iconWrap(
    <>
      <path d="M15 6.5c-1.2-.8-2.7-1-4-.5-1.4.5-2 1.7-1.7 3 .4 1.4 2 1.8 3.7 2.3 1.7.5 3.3 1 3.7 2.4.3 1.4-.4 2.5-1.8 3-1.4.5-3 .3-4.4-.5" />
    </>,
    props,
  );
}

export function ResendGlyph(props) {
  return <Mail {...props} />;
}

export function FalGlyph(props) {
  return iconWrap(
    <>
      <path d="M13 3L4 14h7l-1 7 9-11h-7l1-7z" />
    </>,
    props,
  );
}

export function SoraGlyph(props) {
  return <Video {...props} />;
}

export function WhisperGlyph(props) {
  return <AudioLines {...props} />;
}

export function ElevenLabsGlyph(props) {
  return iconWrap(
    <>
      <rect x="4" y="8" width="3" height="8" />
      <rect x="10" y="4" width="3" height="16" />
      <rect x="16" y="10" width="3" height="4" />
    </>,
    props,
  );
}

export function NanoBananaGlyph(props) {
  return iconWrap(
    <>
      <path d="M6 20c0-8 4-14 12-14-2 8-6 14-12 14z" />
      <path d="M6 20c-1 0-2-1-2-2" />
    </>,
    props,
  );
}

// Map from integration key → { Icon, accent color }
const REGISTRY = {
  openai: { Icon: OpenAIGlyph, accent: "#EDEDED" },
  openai_image: { Icon: OpenAIGlyph, accent: "#0055FF" },
  anthropic: { Icon: ClaudeGlyph, accent: "#F5A623" },
  gemini: { Icon: GeminiGlyph, accent: "#EDEDED" },
  nano_banana: { Icon: NanoBananaGlyph, accent: "#F5A623" },
  sora: { Icon: SoraGlyph, accent: "#0055FF" },
  whisper: { Icon: WhisperGlyph, accent: "#EDEDED" },
  elevenlabs: { Icon: ElevenLabsGlyph, accent: "#F5A623" },
  fal: { Icon: FalGlyph, accent: "#F5A623" },
  github: { Icon: Github, accent: "#EDEDED" },
  gcp: { Icon: GCPGlyph, accent: "#0055FF" },
  clickup: { Icon: ClickUpGlyph, accent: "#4ADE80" },
  stripe: { Icon: StripeGlyph, accent: "#4ADE80" },
  resend: { Icon: ResendGlyph, accent: "#EDEDED" },
  slack: { Icon: Slack, accent: "#0055FF" },
  telegram: { Icon: Send, accent: "#0055FF" },
};

export function getIntegrationVisual(key) {
  return REGISTRY[key] || { Icon: Cloud, accent: "#EDEDED" };
}

// Category accents (for filter chips and card corners)
export const CATEGORY_ACCENT = {
  "LLM · Text": "#F5A623",
  "LLM · Image": "#0055FF",
  "LLM · Video": "#0055FF",
  "Voice": "#F5A623",
  "DevOps": "#EDEDED",
  "Cloud": "#0055FF",
  "Operations": "#4ADE80",
  "Payments": "#4ADE80",
  "Email": "#EDEDED",
  "Communication": "#0055FF",
};

export function categoryColor(category) {
  return CATEGORY_ACCENT[category] || "#EDEDED";
}
